/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState } from "react";
import { Tree, Drop, Wind, Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import PageHeader from "@/components/PageHeader";
import StatBlock from "@/components/StatBlock";
import FieldGuide from "@/components/FieldGuide";
import { fmtNum, fmtUsd } from "@/lib/format";

const METHODS = [
  { id: "Forestry", icon: Tree, color: "text-eco" },
  { id: "DAC", icon: Wind, color: "text-sky2" },
  { id: "Biochar", icon: Drop, color: "text-warn" },
  { id: "Soil", icon: Tree, color: "text-violet2" },
  { id: "Offsets", icon: Drop, color: "text-gold" },
];

export default function Capture() {
  const { active } = useProjects();
  const [items, setItems] = useState([]);
  const [agg, setAgg] = useState({ total_capture_t: 0, total_cost_usd: 0 });
  const [form, setForm] = useState({
    method: "Forestry",
    name: "",
    area_ha: 100,
    capacity_tpa: 0,
    efficiency_pct: 80,
    cost_usd_per_t: 10,
    duration_years: 20,
    registry: "Verra",
    notes: "",
  });

  const load = async () => {
    if (!active) return;
    const { data } = await api.get(`/projects/${active.id}/capture`);
    setItems(data.items);
    setAgg({ total_capture_t: data.total_capture_t, total_cost_usd: data.total_cost_usd });
  };
  useEffect(() => { load(); }, [active]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/projects/${active.id}/capture`, form);
      toast.success("Capture asset added");
      setForm({ ...form, name: "" });
      await load();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  const del = async (id) => {
    await api.delete(`/projects/${active.id}/capture/${id}`);
    await load();
  };

  if (!active) return <div className="p-12 theme-ink-muted">Select a project.</div>;

  return (
    <div className="animate-fade-up">
      <PageHeader
        overline="Module · Carbon Capture & Removal"
        title="Capture, offset, and verify."
        subtitle="Track forestry, direct-air-capture, biochar, soil sequestration and verified offsets against your project's footprint."
        guideId="capture"
      />

      <section className="px-8 pt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatBlock label="Lifetime capture" value={fmtNum(agg.total_capture_t)} unit="tCO₂e" accent="eco" />
        <StatBlock label="Total cost" value={fmtUsd(agg.total_cost_usd)} accent="gold" />
        <StatBlock label="Assets" value={fmtNum(items.length)} accent="white" />
        <StatBlock label="Avg cost / tCO₂e" value={agg.total_capture_t > 0 ? fmtUsd(agg.total_cost_usd / agg.total_capture_t) : "—"} accent="sky" />
      </section>

      <section className="px-8 pt-6 grid grid-cols-1 xl:grid-cols-3 gap-4">
        <form onSubmit={add} className="border theme-border theme-surface p-6 space-y-3" data-testid="capture-form">
          <div className="overline">New capture asset</div>
          <h3 className="font-serif-display text-xl theme-ink">Add</h3>

          <div className="grid grid-cols-2 gap-3">
            {METHODS.map((m) => {
              const Icon = m.icon;
              return (
                <button
                  type="button"
                  key={m.id}
                  data-testid={`method-${m.id}`}
                  onClick={() => setForm({ ...form, method: m.id })}
                  className={`flex items-center gap-2 border px-3 py-2 text-sm rounded-sm transition-colors ${
                    form.method === m.id ? "border-gold bg-gold/10" : "theme-border hover:border-gold/40"
                  }`}
                >
                  <Icon size={14} weight="fill" className={m.color} />
                  <span className="theme-ink">{m.id}</span>
                </button>
              );
            })}
          </div>

          <div>
            <label className="overline flex items-center gap-1 mb-1.5">Name <FieldGuide field="capture.method" /></label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="capture-name-input"
              className="w-full theme-input border px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-gold"
              placeholder="Mangrove Reforestation – Aceh" />
          </div>

          {["Forestry", "Soil"].includes(form.method) && (
            <div>
              <label className="overline flex items-center gap-1 mb-1.5">Area (ha) <FieldGuide field="capture.area_ha" /></label>
              <input type="number" value={form.area_ha} onChange={(e) => setForm({ ...form, area_ha: Number(e.target.value) })}
                className="w-full theme-input border px-3 py-2 text-sm rounded-sm font-mono-data focus:outline-none focus:border-gold" />
            </div>
          )}

          {["DAC", "Biochar", "Offsets"].includes(form.method) && (
            <div>
              <label className="overline flex items-center gap-1 mb-1.5">Capacity (tCO₂e/yr) <FieldGuide field="capture.capacity_tpa" /></label>
              <input type="number" value={form.capacity_tpa} onChange={(e) => setForm({ ...form, capacity_tpa: Number(e.target.value) })}
                className="w-full theme-input border px-3 py-2 text-sm rounded-sm font-mono-data focus:outline-none focus:border-gold" />
            </div>
          )}

          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="overline flex items-center gap-1 mb-1.5">Eff % <FieldGuide field="capture.efficiency_pct" /></label>
              <input type="number" value={form.efficiency_pct} onChange={(e) => setForm({ ...form, efficiency_pct: Number(e.target.value) })}
                className="w-full theme-input border px-3 py-2 text-sm rounded-sm font-mono-data focus:outline-none focus:border-gold" />
            </div>
            <div>
              <label className="overline flex items-center gap-1 mb-1.5">$/t <FieldGuide field="capture.cost_usd_per_t" /></label>
              <input type="number" value={form.cost_usd_per_t} onChange={(e) => setForm({ ...form, cost_usd_per_t: Number(e.target.value) })}
                className="w-full theme-input border px-3 py-2 text-sm rounded-sm font-mono-data focus:outline-none focus:border-gold" />
            </div>
            <div>
              <label className="overline mb-1.5 block">Years</label>
              <input type="number" value={form.duration_years} onChange={(e) => setForm({ ...form, duration_years: Number(e.target.value) })}
                className="w-full theme-input border px-3 py-2 text-sm rounded-sm font-mono-data focus:outline-none focus:border-gold" />
            </div>
          </div>

          <div>
            <label className="overline block mb-1.5">Registry</label>
            <input value={form.registry} onChange={(e) => setForm({ ...form, registry: e.target.value })}
              className="w-full theme-input border px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-gold"
              placeholder="Verra, Gold Standard…" />
          </div>

          <button type="submit" data-testid="capture-add-btn"
            className="w-full bg-gold text-black font-medium py-2.5 rounded-sm hover:bg-[#e0bc40] flex items-center justify-center gap-2">
            <Plus size={16} weight="bold" /> Add asset
          </button>
        </form>

        <div className="xl:col-span-2 border theme-border theme-surface overflow-hidden">
          <div className="px-6 py-4 border-b theme-border">
            <div className="overline">Portfolio</div>
            <div className="font-serif-display text-xl theme-ink">{items.length} capture assets</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black/[0.02] text-[10px] uppercase tracking-[0.2em] theme-ink-dim">
                <tr>
                  <th className="text-left px-6 py-3">Name</th>
                  <th className="text-left px-3 py-3">Method</th>
                  <th className="text-right px-3 py-3">Annual</th>
                  <th className="text-right px-3 py-3">Lifetime</th>
                  <th className="text-right px-3 py-3">$/t</th>
                  <th className="text-right px-6 py-3">Total cost</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} className="border-t theme-border">
                    <td className="px-6 py-3">
                      <div className="theme-ink">{c.name}</div>
                      <div className="text-[11px] theme-ink-dim">{c.registry}</div>
                    </td>
                    <td className="px-3 theme-ink-muted">{c.method}</td>
                    <td className="px-3 py-3 text-right font-mono-data">{fmtNum(c.annual_capture_t, 1)}</td>
                    <td className="px-3 py-3 text-right font-mono-data text-eco">{fmtNum(c.lifetime_capture_t, 0)}</td>
                    <td className="px-3 py-3 text-right font-mono-data theme-ink-muted">${c.cost_usd_per_t}</td>
                    <td className="px-6 py-3 text-right font-mono-data text-gold">{fmtUsd(c.lifetime_cost_usd)}</td>
                    <td className="px-3 py-3 text-right">
                      <button onClick={() => del(c.id)} className="theme-ink-dim hover:text-risk">
                        <Trash size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan={7} className="px-6 py-10 text-center theme-ink-dim">No capture assets yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
      <div className="pb-12" />
    </div>
  );
}
